import React from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getResearchTypeById } from '@/types/research-types';
import { stripMarkdownAndTruncate } from '@/components/research-generator/research-matrix-utils/content-utils';
import AddToLibraryButton from '@/components/universal-storage/AddToLibraryButton';
import ResearchAddToProjectButton from '@/components/private-research-generator/AddToProjectButton';

interface ProjectResearchItem {
  id: string;
  title: string;
  content: string;
  research_type: string;
  created_at: string;
  is_favorited?: boolean;
  favorite_count?: number;
  research_parameters?: {
    source_type?: string;
    timeframe?: string;
    country?: string;
  };
}

interface ProjectResearchCardProps {
  research: ProjectResearchItem;
  index: number;
  onResearchClick: (researchId: string) => void;
  onToggleFavorite: (researchId: string) => Promise<void>;
}

export const ProjectResearchCard = React.forwardRef<HTMLDivElement, ProjectResearchCardProps>(({
  research,
  index,
  onResearchClick,
  onToggleFavorite,
}, ref) => {
  const location = useLocation();
  
  // Hide storage buttons on project pages, show them on storage pages
  const isStoragePage = location.pathname.includes('/storage');
  
  const researchType = getResearchTypeById(research.research_type);
  const formattedDate = formatDistanceToNow(new Date(research.created_at), { addSuffix: true });
  const contentSnippet = stripMarkdownAndTruncate(research.content, 150);

  return (
    <motion.div 
      ref={ref}
      key={research.id}
      layoutId={`project-research-${research.id}`}
      className="group relative cursor-pointer"
      initial={{ 
        opacity: 0, 
        scale: 0.9,
        y: 10
      }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: 0
      }}
      exit={{ 
        opacity: 0, 
        scale: 0.9,
        y: -10
      }}
      whileHover={{ 
        scale: 1.02,
        y: -2
      }}
      transition={{
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1.0],
        layout: {
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1.0]
        }
      }}
      onClick={() => onResearchClick(research.id)}
    >
      {/* Modern card with glass morphism effect */}
      <motion.div 
        className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 rounded-2xl p-3 md:p-6 h-full shadow-lg flex flex-col"
        whileHover={{
          borderColor: 'rgba(34, 197, 94, 0.3)', // green-500/30 for research theme
          boxShadow: '0 25px 50px -12px rgba(34, 197, 94, 0.15)'
        }}
        transition={{
          duration: 0.2,
          ease: [0.25, 0.1, 0.25, 1.0]
        }}
      >
        {/* Header with research type */}
        <div className="flex items-center justify-between mb-4">
          <div className="rounded-full font-medium backdrop-blur-sm border shadow-sm transition-all duration-300 font-sans bg-green-100/70 dark:bg-green-900/30 border-green-200 dark:border-green-800/30 text-green-700 dark:text-green-300 text-xs font-medium px-3 py-1">
            {researchType?.name || 'Research'}
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 line-clamp-2 leading-tight">
            {research.title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 leading-relaxed">
            {contentSnippet}
          </p>
        </div>
        
        {/* Footer - Standardized layout matching Private Research */}
        <div className="mt-auto pt-2">
          <div className="flex items-end justify-between">
            {/* Date on the left side (with green dot for green aesthetic) */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"></div>
                <span className="text-gray-600 dark:text-gray-400">{formattedDate}</span>
              </div>
            </div>
            
            {/* Action buttons on the right side (no favorites, no source type) */}
            <div className="flex items-center gap-2">
              {/* Add to Project button */}
              <ResearchAddToProjectButton
                itemId={research.id}
                variant="auto"
                dropdownLabel="Add to Project"
              />
              
              {/* Storage button */}
              {isStoragePage && (
                <>
                  <AddToLibraryButton
                    itemId={research.id}
                    contentType="private_research"
                    dropdownLabel="Add to Storage"
                    variant="auto"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
});

// Add display name for better debugging
ProjectResearchCard.displayName = 'ProjectResearchCard';

export default ProjectResearchCard; 